import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

import { MatchActionTakenSchema } from '../match-action/match-action';
import { SignalMatchStatusSchema } from '../signal-match-status/signal-match-status';
import { CapturePointSchema, IdentitySignalTypeSchema } from '../signal-type/signal-type';

/**
 * One recorded collision between a captured signal and a **banned** account's
 * signal of the same type (`03-trust-ban-evasion.md` §pipeline).
 *
 * Written on both branches — `flag` raises it `pending_review`, `block` raises
 * it already `blocked` — because "everything flagged or blocked is visible and
 * reversible in the Trust queue" is what makes a blocking default acceptable at
 * all. A refusal nobody can find is a refusal nobody can undo.
 *
 * **`subjectUserId` is nullable, and that is load-bearing.** A registration
 * blocked by this pipeline is refused *before* the user row is written, so there
 * is no account to point at — the whole purpose of blocking is that the account
 * never came into existence. Requiring the column would have forced the
 * pipeline to create the very row it is refusing. `matchedUserId` is never null:
 * the banned side is what was matched against, and it always exists.
 *
 * **No value travels on this shape** — not the hash that matched, not the
 * embedding. A reviewer is told *that* two accounts collided on a signal type
 * and, for a fuzzy type, how closely; the values themselves are of no use to a
 * human verdict and every copy of them is a place they can leak from.
 */
export const SignalMatchSchema = z
  .object({
    id: UuidSchema,
    /** Null when a blocked registration meant no account was ever created. */
    subjectUserId: UuidSchema.nullable(),
    /** The banned account whose signal was matched. Always present. */
    matchedUserId: UuidSchema,
    signalType: IdentitySignalTypeSchema,
    capturePoint: CapturePointSchema,
    /**
     * Cosine similarity for a fuzzy type; **null** for an equality match.
     *
     * Null rather than 1: an exact hash match has no degree, and printing a
     * similarity of 1.0 beside it would invite a reviewer to read a measurement
     * into a comparison that never made one.
     */
    score: z.number().min(-1).max(1).nullable(),
    /**
     * The threshold in force when the comparison was made — a **snapshot**, for
     * the same reason `face_match_checks.thresholdAtTime` is one: an operator
     * retuning `trust.signals` must not silently re-judge matches already
     * recorded. Pinned at 1 for exact types.
     */
    thresholdAtTime: z.number().min(0).max(1),
    actionTaken: MatchActionTakenSchema,
    status: SignalMatchStatusSchema,
    matchedAt: z.iso.datetime(),
    reviewedAt: z.iso.datetime().nullable(),
    reviewedByUserId: UuidSchema.nullable(),
    /** The reviewer's own note. Staff-facing only — no member ever reads it. */
    reviewNote: z.string().nullable(),
  })
  .strict();

export type SignalMatch = z.infer<typeof SignalMatchSchema>;
